import React, { useEffect, useState, useCallback } from "react";
import { api, API_BASE, euro, formatApiError } from "../lib/api";
import {
  Plus, X, User, Mail, Phone, ArrowLeft, Trash2, FileDown,
  Heart, Pill, Target, AlertTriangle, NotebookPen, Save,
  CalendarDays, FlaskConical,
} from "lucide-react";
import { toast } from "sonner";

const emptyClient = {
  name: "", email: "", phone: "", address: "",
  session_type: "", notes: "", sms_opt_in: true,
  is_business: false, company_name: "", siret: "", vat_number: "",
  date_of_birth: "", medical_history: "", allergies: "",
  current_treatment: "", prescriptions: "", initial_motive: "", objectives: "",
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyClient);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch (e) {
      console.error("Failed to load clients", e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setError("");
    if (!form.name) return setError("Le nom est requis.");
    try {
      await api.post("/clients", form);
      setShowCreate(false);
      setForm(emptyClient);
      load();
      toast.success("Client ajouté");
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  };

  const open = async (id) => {
    const { data } = await api.get(`/clients/${id}`);
    setSelected(data);
  };

  const removeClient = async (id) => {
    if (!window.confirm("Supprimer ce client ? Ses séances ne seront pas effacées mais elles n'auront plus de fiche associée.")) return;
    await api.delete(`/clients/${id}`);
    setSelected(null);
    load();
  };

  if (selected) return (
    <ClientDetail
      c={selected}
      onBack={() => { setSelected(null); load(); }}
      onDelete={() => removeClient(selected.id)}
      onReload={async () => {
        const { data } = await api.get(`/clients/${selected.id}`);
        setSelected(data);
      }}
    />
  );

  return (
    <div className="space-y-6" data-testid="clients-container">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Clients</h1>
          <p className="text-muted-soft mt-1">Vos clients, dossiers et historique.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-sage text-sm" data-testid="add-client-btn">
          <Plus className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} />
          Nouveau client
        </button>
      </div>

      {clients.length === 0 ? (
        <div className="card-soft p-12 text-center" data-testid="clients-empty">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-sage-light flex items-center justify-center mb-4">
            <User className="w-7 h-7 text-sage" strokeWidth={1.4} />
          </div>
          <div className="font-display text-xl mb-2">Aucun client pour le moment</div>
          <p className="text-muted-soft mb-6">Commencez par ajouter votre premier client.</p>
          <button onClick={() => setShowCreate(true)} className="btn-sage text-sm" data-testid="add-client-empty-btn">Ajouter un client</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <button key={c.id} onClick={() => open(c.id)} className="card-soft card-soft-hover p-6 text-left" data-testid={`client-card-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-full bg-beige flex items-center justify-center font-medium" style={{ color: "#3A352D" }}>
                  {c.name.split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <span className="text-xs text-muted-soft">{c.sessions_count || 0} séance(s)</span>
              </div>
              <div className="font-display text-lg mb-1">{c.name}</div>
              {c.session_type && <div className="text-xs text-muted-soft mb-2">{c.session_type}</div>}
              <div className="space-y-1 text-xs text-muted-soft">
                {c.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3" strokeWidth={1.6} />{c.email}</div>}
                {c.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" strokeWidth={1.6} />{c.phone}</div>}
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Nouveau client" testid="create-client-modal">
          <div className="space-y-5">
            <Section title="Identité">
              <label className="flex items-start gap-3 cursor-pointer text-sm py-1 -mt-1">
                <input type="checkbox" checked={!!form.is_business}
                  onChange={(e) => setForm({ ...form, is_business: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-sage" data-testid="client-is-business" />
                <span>
                  <b>Client professionnel</b> (entreprise, club, hôpital, association…)<br/>
                  <span className="text-[11px] text-muted-soft leading-relaxed">
                    Coche cette case pour saisir raison sociale + SIRET du client (facturation B2B conforme Factur-X EN 16931).
                  </span>
                </span>
              </label>

              {form.is_business && (
                <div className="space-y-3 p-3 rounded-xl border" style={{ borderColor: "var(--border)", background: "var(--secondary)" }}>
                  <Field label="Raison sociale *">
                    <input className="input-soft" placeholder="Ex : Club Sport Paris SAS"
                      value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      data-testid="client-company-name" />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="SIRET du client">
                      <input className="input-soft" placeholder="14 chiffres"
                        value={form.siret}
                        onChange={(e) => setForm({ ...form, siret: e.target.value })}
                        data-testid="client-siret" />
                    </Field>
                    <Field label="N° TVA intracom (optionnel)">
                      <input className="input-soft" placeholder="FR + 11 chiffres"
                        value={form.vat_number}
                        onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                        data-testid="client-vat-number" />
                    </Field>
                  </div>
                </div>
              )}

              <Field label={form.is_business ? "Personne de contact *" : "Nom complet *"}>
                <input className="input-soft" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Date de naissance">
                  <input type="date" className="input-soft" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="client-dob" />
                </Field>
                <Field label="Type de séance">
                  <input className="input-soft" placeholder="Sophrologie, massage..." value={form.session_type} onChange={(e) => setForm({ ...form, session_type: e.target.value })} data-testid="client-type" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Email"><input type="email" className="input-soft" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" /></Field>
                <Field label="Téléphone"><input className="input-soft" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone" /></Field>
              </div>
              <Field label={form.is_business ? "Adresse de facturation" : "Adresse (optionnelle)"}>
                <input className="input-soft" placeholder="12 rue de la Paix, 75002 Paris, France"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="client-address" />
              </Field>
            </Section>

            <Section title="Suivi d'accompagnement" subtitle="Informations non médicales uniquement · chiffrées au repos">
              <div
                className="text-xs leading-relaxed rounded-xl px-4 py-3 border"
                style={{ background: "#FFF8E7", borderColor: "#E8D9A3", color: "#5C4A15" }}
                data-testid="medical-warning-create"
              >
                <b>⚠️ Ne renseignez dans cet espace aucune donnée médicale ou donnée de santé sensible</b> :
                diagnostic, pathologie, traitement, prescription, résultats d'examens, antécédents médicaux, etc.
                Cet espace est destiné uniquement aux informations nécessaires au suivi général de votre accompagnement.
              </div>
              <Field label="Motif d'accompagnement">
                <textarea className="input-soft" style={{ minHeight: 110, paddingTop: 12 }} value={form.initial_motive} onChange={(e) => setForm({ ...form, initial_motive: e.target.value })} data-testid="client-motive" />
              </Field>
              <Field label="Objectifs du suivi">
                <textarea className="input-soft" style={{ minHeight: 110, paddingTop: 12 }} value={form.objectives} onChange={(e) => setForm({ ...form, objectives: e.target.value })} data-testid="client-objectives" />
              </Field>
              <Field label="Notes générales">
                <textarea className="input-soft" style={{ minHeight: 130, paddingTop: 12 }} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="client-notes" />
              </Field>
            </Section>

            <label className="flex items-center gap-3 cursor-pointer text-sm">
              <input type="checkbox" checked={form.sms_opt_in !== false} onChange={(e) => setForm({ ...form, sms_opt_in: e.target.checked })} className="w-4 h-4 accent-sage" data-testid="client-sms-opt-in" />
              <span>Recevoir les rappels SMS (confirmation + veille du RDV)</span>
            </label>

            {error && <div className="text-sm text-clay">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowCreate(false)} className="btn-ghost text-sm" data-testid="client-cancel">Annuler</button>
              <button onClick={create} className="btn-sage text-sm" data-testid="client-save">Enregistrer</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatusBadge({ status, payment }) {
  const s = status === "cancelled" ? { label: "Annulée", color: "var(--text-muted)", bg: "var(--sage-light)" }
    : payment === "paid" ? { label: "Payée", color: "var(--sage)", bg: "var(--sage-light)" }
    : payment === "to_chase" ? { label: "À relancer", color: "#8B4D3D", bg: "#F5E5DC" }
    : status === "done" ? { label: "À encaisser", color: "#8B6F4D", bg: "#F5E8D8" }
    : { label: "Prévue", color: "#3A352D", bg: "var(--secondary)" };
  return <span className="text-xs px-2.5 py-1 rounded-full" style={{ color: s.color, backgroundColor: s.bg }}>{s.label}</span>;
}

function ClientDetail({ c, onBack, onDelete, onReload }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(c);
  const [openNoteId, setOpenNoteId] = useState(null);

  useEffect(() => { setForm(c); }, [c]);

  const saveClient = async () => {
    try {
      // Champs médicaux (medical_history, allergies, current_treatment, prescriptions)
      // volontairement NON envoyés — l'infra Emergent n'étant pas certifiée HDS,
      // ces champs ne sont plus accessibles depuis l'UI. Les données déjà stockées
      // restent intactes en base (l'audit admin les recense).
      await api.put(`/clients/${c.id}`, {
        name: form.name, email: form.email, phone: form.phone, address: form.address,
        is_business: form.is_business, company_name: form.company_name,
        siret: form.siret, vat_number: form.vat_number,
        date_of_birth: form.date_of_birth, session_type: form.session_type,
        initial_motive: form.initial_motive, objectives: form.objectives,
        notes: form.notes, sms_opt_in: form.sms_opt_in,
      });
      toast.success("Dossier mis à jour");
      setEditing(false);
      onReload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadDossier = async () => {
    const token = localStorage.getItem("mcb_token");
    const res = await fetch(`${API_BASE}/clients/${c.id}/dossier-pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      toast.error("Erreur de téléchargement du dossier");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dossier_${(c.name || "client").replace(/\s+/g, "_")}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="client-detail">
      <button onClick={onBack} className="text-sm text-muted-soft hover:text-sage flex items-center gap-1.5" data-testid="client-back">
        <ArrowLeft className="w-4 h-4" /> Retour aux clients
      </button>

      <div className="card-soft p-6 sm:p-7">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-16 h-16 rounded-full bg-beige flex items-center justify-center font-display text-xl flex-shrink-0" style={{ color: "#3A352D" }}>
              {c.name.split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
            </div>
            <div className="min-w-0">
              <h1 className="font-display text-2xl truncate">{c.name}</h1>
              {c.session_type && <div className="text-sm text-muted-soft mt-0.5 truncate">{c.session_type}</div>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            {!editing ? (
              <button onClick={() => setEditing(true)} className="btn-sage text-sm flex-1 sm:flex-initial" data-testid="edit-client-btn">
                <NotebookPen className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} /> Modifier la fiche
              </button>
            ) : (
              <button onClick={saveClient} className="btn-sage text-sm flex-1 sm:flex-initial" data-testid="edit-client-save">
                <Save className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} /> Enregistrer
              </button>
            )}
            <button onClick={downloadDossier} className="btn-ghost text-sm" data-testid="dossier-pdf-btn">
              <FileDown className="inline w-4 h-4 mr-1.5" strokeWidth={1.8} /> PDF
            </button>
            <button onClick={onDelete} className="p-2 rounded-xl hover:bg-sage-light" title="Supprimer" data-testid="client-delete">
              <Trash2 className="w-4 h-4 text-clay" strokeWidth={1.6} />
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          {c.email && <div className="flex items-center gap-2 text-muted-soft"><Mail className="w-4 h-4" strokeWidth={1.6} />{c.email}</div>}
          {c.phone && <div className="flex items-center gap-2 text-muted-soft"><Phone className="w-4 h-4" strokeWidth={1.6} />{c.phone}</div>}
          {c.date_of_birth && <div className="flex items-center gap-2 text-muted-soft"><CalendarDays className="w-4 h-4" strokeWidth={1.6} />Né(e) le {c.date_of_birth}</div>}
        </div>
      </div>

      {/* Suivi d'accompagnement (ex-Dossier médical) */}
      <div className="card-soft p-6 sm:p-7" data-testid="anamnese-card">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="font-display text-lg">Suivi d'accompagnement</h2>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="btn-sage text-xs" data-testid="anamnese-edit">
              <NotebookPen className="inline w-3.5 h-3.5 mr-1" strokeWidth={1.8} /> Modifier
            </button>
          ) : (
            <button onClick={saveClient} className="btn-sage text-xs" data-testid="anamnese-save">
              <Save className="inline w-3.5 h-3.5 mr-1" /> Enregistrer
            </button>
          )}
        </div>

        <div
          className="text-xs leading-relaxed rounded-xl px-4 py-3 mb-4 border"
          style={{ background: "#FFF8E7", borderColor: "#E8D9A3", color: "#5C4A15" }}
          data-testid="medical-warning"
        >
          <b>⚠️ Ne renseignez dans cet espace aucune donnée médicale ou donnée de santé sensible</b> :
          diagnostic, pathologie, traitement, prescription, résultats d'examens, antécédents médicaux, etc.
          Cet espace est destiné uniquement aux informations nécessaires au suivi général de votre accompagnement
          (motif d'accompagnement, objectifs, notes de séance).
        </div>

        {!editing && (
          <p className="text-xs text-muted-soft mb-3">
            Tapez sur « Modifier » pour renseigner ou compléter les champs ci-dessous.
          </p>
        )}

        <div className="space-y-4">
          <DossierField editing={editing} icon={Target} label="Motif d'accompagnement" value={c.initial_motive} formValue={form.initial_motive} onChange={(v) => setForm({ ...form, initial_motive: v })} testid="field-motive" />
          <DossierField editing={editing} icon={Heart} label="Objectifs du suivi" value={c.objectives} formValue={form.objectives} onChange={(v) => setForm({ ...form, objectives: v })} testid="field-objectives" />
          <DossierField editing={editing} icon={NotebookPen} label="Notes générales" value={c.notes} formValue={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="field-notes" />
        </div>
      </div>

      {/* Sessions */}
      <div className="card-soft p-6 sm:p-7">
        <div className="font-display text-lg mb-4">Historique des séances ({c.sessions?.length || 0})</div>
        {!c.sessions || c.sessions.length === 0 ? (
          <div className="text-sm text-muted-soft">Aucune séance enregistrée pour ce client.</div>
        ) : (
          <div className="space-y-2">
            {c.sessions.map((s) => (
              <SessionNoteRow
                key={s.id}
                session={s}
                isOpen={openNoteId === s.id}
                onToggle={() => setOpenNoteId(openNoteId === s.id ? null : s.id)}
                onSaved={onReload}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DossierField({ editing, icon: Icon, label, value, formValue, onChange, testid, highlight }) {
  const empty = !value || !String(value).trim();
  return (
    <div className="border-t pt-3 first:border-0 first:pt-0" style={{ borderColor: "var(--border)" }}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-3.5 h-3.5 text-sage" strokeWidth={1.6} />
        <div className="text-xs uppercase tracking-wider text-muted-soft font-medium">{label}</div>
      </div>
      {editing ? (
        <textarea
          className="input-soft" style={{ minHeight: 120, paddingTop: 10 }}
          value={formValue || ""} onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
      ) : empty ? (
        <div className="text-sm text-muted-soft italic">Non renseigné</div>
      ) : (
        <div
          className="text-sm whitespace-pre-wrap"
          style={highlight ? { backgroundColor: "#F5E5DC", padding: 10, borderRadius: 10, color: "#8B4D3D" } : {}}
          data-testid={`${testid}-value`}
        >
          {value}
        </div>
      )}
    </div>
  );
}

function SessionNoteRow({ session, isOpen, onToggle, onSaved }) {
  const [form, setForm] = useState({
    session_note_observations: session.session_note_observations || "",
    session_note_exercises: session.session_note_exercises || "",
    session_note_advice: session.session_note_advice || "",
    session_note_next_steps: session.session_note_next_steps || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      session_note_observations: session.session_note_observations || "",
      session_note_exercises: session.session_note_exercises || "",
      session_note_advice: session.session_note_advice || "",
      session_note_next_steps: session.session_note_next_steps || "",
    });
  }, [session]);

  const hasAnyNote = Object.values(form).some((v) => String(v).trim());

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/sessions/${session.id}`, form);
      toast.success("Notes de séance enregistrées");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div className="rounded-xl border" style={{ borderColor: "var(--border)" }} data-testid={`session-note-row-${session.id}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-sage-light/30 transition-colors text-left">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-1.5 h-10 rounded-full" style={{ backgroundColor: hasAnyNote ? "var(--sage)" : "var(--border)" }} />
          <div className="min-w-0">
            <div className="text-sm font-medium">{new Date(session.starts_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
            <div className="text-xs text-muted-soft truncate">{session.session_type || "Séance"} · {hasAnyNote ? "Notes enregistrées" : "Pas de note"}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-sm font-medium">{euro(session.price)}</span>
          <StatusBadge status={session.status} payment={session.payment_status} />
        </div>
      </button>

      {isOpen && (
        <div className="px-4 pb-4 pt-2 space-y-3 border-t" style={{ borderColor: "var(--border)" }}>
          <div
            className="text-xs leading-relaxed rounded-xl px-3 py-2.5 border"
            style={{ background: "#FFF8E7", borderColor: "#E8D9A3", color: "#5C4A15" }}
            data-testid={`session-note-warning-${session.id}`}
          >
            <b>⚠️ Aucune donnée médicale ou de santé sensible dans les notes de séance</b> — utilisez uniquement des informations d'accompagnement général.
          </div>
          <NoteArea label="📝 Observations pendant la séance" value={form.session_note_observations} onChange={(v) => setForm({ ...form, session_note_observations: v })} testid={`note-obs-${session.id}`} />
          <NoteArea label="🧘 Exercices / techniques utilisées" value={form.session_note_exercises} onChange={(v) => setForm({ ...form, session_note_exercises: v })} testid={`note-exo-${session.id}`} />
          <NoteArea label="💬 Conseils donnés au client" value={form.session_note_advice} onChange={(v) => setForm({ ...form, session_note_advice: v })} testid={`note-advice-${session.id}`} />
          <NoteArea label="⏭️ À prévoir pour la prochaine séance" value={form.session_note_next_steps} onChange={(v) => setForm({ ...form, session_note_next_steps: v })} testid={`note-next-${session.id}`} />
          <div className="flex justify-end pt-1">
            <button onClick={save} disabled={saving} className="btn-sage text-sm" data-testid={`note-save-${session.id}`}>
              <Save className="inline w-3.5 h-3.5 mr-1.5" /> {saving ? "Enregistrement…" : "Enregistrer les notes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NoteArea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-medium block mb-1.5 text-muted-soft">{label}</label>
      <textarea
        className="input-soft" style={{ minHeight: 110, paddingTop: 10 }}
        value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div className="font-display text-base text-sage mb-1">{title}</div>
      {subtitle && <div className="text-xs text-muted-soft mb-3">{subtitle}</div>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Modal({ onClose, title, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl card-soft p-6 max-h-[90vh] overflow-y-auto scroll-soft">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-xl">{title}</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-sage-light"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
